import React, { useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReactMarkdown from "react-markdown";
import { API_get_assistant_question_report } from "../../../../../constants";
import { postRightworkAPI } from "../../../requests/utilities/requests";
import PrebuiltReportShell from "../PrebuiltReportShell";

const SOURCE_OPTIONS = [
  { value: "rovis", label: "Rovis" },
  { value: "knowledge_base", label: "Company Files" },
  { value: "knowledge_base_voice", label: "Company Files Voice" },
];

const REPORT_VIOLET = "#6d28d9";
const REPORT_VIOLET_DARK = "#5b21b6";
const REPORT_VIOLET_LIGHT = "#f3e8ff";
const REPORT_BORDER = "#cbd5e1";
const CONTROL_HEIGHT = 36;

const checkboxSx = {
  color: "#94a3b8",
  "&.Mui-checked": { color: REPORT_VIOLET },
  "&.MuiCheckbox-indeterminate": { color: REPORT_VIOLET },
};

const selectInputSx = {
  height: CONTROL_HEIGHT,
  fontSize: 13,
  borderRadius: "8px",
  backgroundColor: "white",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_BORDER,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94a3b8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_VIOLET,
    borderWidth: "1px",
  },
};

const selectSx = {
  width: "100%",
  height: CONTROL_HEIGHT,
  borderRadius: "8px",
  backgroundColor: "white",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: REPORT_BORDER,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94a3b8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${REPORT_VIOLET} !important`,
    borderWidth: "1px !important",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: "0 !important",
    paddingTop: "8px",
    paddingBottom: "8px",
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.75,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 14px 34px rgba(15, 23, 42, 0.14)",
      "& .MuiMenuItem-root": {
        fontSize: 13,
        minHeight: 36,
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: REPORT_VIOLET_LIGHT,
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "#ede9fe",
      },
      "& .MuiMenuItem-root.Mui-focusVisible": {
        backgroundColor: REPORT_VIOLET_LIGHT,
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#f8fafc",
      },
    },
  },
};

const datePickerPopperSx = {
  "& .MuiPaper-root": {
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: `${REPORT_VIOLET} !important`,
    color: "white",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: `${REPORT_VIOLET_DARK} !important`,
  },
  "& .MuiPickersDay-root.Mui-focusVisible": {
    backgroundColor: REPORT_VIOLET_LIGHT,
  },
  "& .MuiPickersDay-root.MuiPickersDay-today": {
    borderColor: REPORT_VIOLET,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: `${REPORT_VIOLET} !important`,
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: `${REPORT_VIOLET} !important`,
  },
};

const fullTextTooltipProps = {
  arrow: true,
  placement: "top-start",
  enterDelay: 300,
  componentsProps: {
    tooltip: {
      sx: {
        maxWidth: 680,
        maxHeight: 420,
        overflowY: "auto",
        bgcolor: "white",
        color: "#334155",
        border: "1px solid #cbd5e1",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
        fontSize: 12,
        lineHeight: 1.55,
        whiteSpace: "normal",
        p: 1.5,
      },
    },
    arrow: {
      sx: {
        color: "white",
        "&:before": {
          border: "1px solid #cbd5e1",
        },
      },
    },
  },
};

function defaultDateRange() {
  const end = dayjs().startOf("day");
  const start = end.subtract(29, "day");
  return { start, end };
}

function uniqueBy(items, keyFn) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = keyFn(item);
    if (key == null || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function getCompanyName(store) {
  return (
    store?.company_info?.company_name ||
    store?.company_info?.name ||
    store?.company_name ||
    `Company ${store?.company || ""}`.trim()
  );
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${Math.round(n * 100)}%`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return parsed.format("MMM D, YYYY h:mm A");
}

function truncate(value, max = 220) {
  if (!value) return "-";
  const text = String(value).replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}...`;
}

function statusClass(status) {
  if (status === "answered") return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (status === "no_answer") return "bg-amber-50 text-amber-700 border-amber-100";
  if (status === "error") return "bg-red-50 text-red-700 border-red-100";
  return "bg-slate-50 text-slate-600 border-slate-100";
}

function actionOutcomeClass(outcome) {
  if (outcome === "accepted") return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (outcome === "canceled") return "bg-slate-50 text-slate-600 border-slate-100";
  if (outcome === "pending") return "bg-amber-50 text-amber-700 border-amber-100";
  if (outcome === "mixed" || outcome === "partial") return "bg-violet-50 text-violet-700 border-violet-100";
  if (outcome === "failed") return "bg-red-50 text-red-700 border-red-100";
  return "bg-slate-50 text-slate-400 border-slate-100";
}

function actionTooltip(row) {
  const proposals = Array.isArray(row.action_proposals) ? row.action_proposals : [];
  if (proposals.length === 0) return "";
  return proposals
    .map((proposal) => {
      const title = proposal.title || proposal.tool_name || "Action";
      const detail = proposal.detail ? ` — ${proposal.detail}` : "";
      return `${proposal.status_label || "Pending"}: ${title}${detail}`;
    })
    .join("\n");
}

function ActionOutcomeCell({ row }) {
  if (!row.action_proposal_count) {
    return <span className="text-slate-400">-</span>;
  }

  const label = row.action_outcome_label || row.action_outcome || "Action";
  const detail = row.action_summary_label || `${row.action_proposal_count} proposed`;
  const chip = (
    <span className={`inline-flex px-2 py-0.5 rounded border ${actionOutcomeClass(row.action_outcome)}`}>
      {label}
    </span>
  );
  const tooltip = actionTooltip(row);

  return (
    <div className="space-y-1">
      {tooltip ? (
        <Tooltip
          title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
          arrow
          placement="top-start"
        >
          {chip}
        </Tooltip>
      ) : chip}
      <div className="text-[11px] leading-tight text-slate-500">{detail}</div>
    </div>
  );
}

function renderMultiValue(selected, allItems, pluralLabel, singularLabel, itemLabel = "name") {
  if (!Array.isArray(selected) || selected.length === 0) return `All ${pluralLabel}`;
  if (selected.length === allItems.length) return `All ${pluralLabel}`;
  if (selected.length === 1) {
    const found = allItems.find((item) => String(item.id) === String(selected[0]));
    return found?.[itemLabel] || `1 ${singularLabel}`;
  }
  return `${selected.length} ${pluralLabel}`;
}

function SummaryCard({ label, value, detail }) {
  return (
    <div className="border border-slate-200 rounded-lg px-4 py-3 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 mt-1">{value}</div>
      {detail && <div className="text-xs text-slate-500 mt-1">{detail}</div>}
    </div>
  );
}

function formatTooltipMarkdown(value) {
  if (!value) return "-";
  return String(value)
    .replace(/\s+(\d+)\.\s+(?=\*\*|[A-Z0-9])/g, "\n\n$1. ")
    .replace(/\s+-\s+(Clock-in:|Clock-out:|Flags?:)/g, "\n   - $1")
    .replace(/\s+(There (?:were|was)\s+\*\*)/g, "\n\n$1")
    .replace(/\s+(If you want,)/g, "\n\n$1")
    .trim();
}

function FullTextTooltipContent({ value }) {
  return (
    <div className="max-w-full text-xs leading-relaxed text-slate-700">
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-2" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-4 mt-1 space-y-1" {...props} />,
          li: ({ node, ...props }) => <li className="pl-1" {...props} />,
        }}
      >
        {formatTooltipMarkdown(value)}
      </ReactMarkdown>
    </div>
  );
}

function FullTextCell({ value, max, className }) {
  const text = value ? String(value) : "-";
  const clipped = truncate(text, max);

  if (!value || clipped === text) {
    return <span className={className}>{clipped}</span>;
  }

  return (
    <Tooltip title={<FullTextTooltipContent value={text} />} {...fullTextTooltipProps}>
      <span className={`${className || ""} cursor-help decoration-dotted underline-offset-2 hover:underline`}>
        {clipped}
      </span>
    </Tooltip>
  );
}

export default function AssistantQuestionsReport({
  report,
  onBack,
  homeCompany,
  storeNames,
}) {
  const initial = useMemo(defaultDateRange, []);
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() =>
    homeCompany ? [homeCompany] : []
  );
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [selectedSources, setSelectedSources] = useState(
    SOURCE_OPTIONS.map((source) => source.value)
  );
  const [payload, setPayload] = useState(null);
  const [hasRun, setHasRun] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const companyOptions = useMemo(() => {
    const stores = Array.isArray(storeNames) ? storeNames : [];
    return uniqueBy(
      stores
        .filter((store) => store?.company != null)
        .map((store) => ({
          id: store.company,
          name: getCompanyName(store),
        })),
      (company) => company.id
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [storeNames]);

  const storeOptions = useMemo(() => {
    const stores = Array.isArray(storeNames) ? storeNames : [];
    const companyFilter = new Set(selectedCompanyIds.map(String));
    return stores
      .filter((store) => {
        if (selectedCompanyIds.length === 0) return true;
        return companyFilter.has(String(store.company));
      })
      .map((store) => ({
        id: store.id,
        name: store.store_name || store.name || `Store ${store.id}`,
        companyId: store.company,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [storeNames, selectedCompanyIds]);

  const datePickerSx = {
    width: "100%",
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      height: CONTROL_HEIGHT,
      backgroundColor: "white",
      borderRadius: "8px",
      fontSize: "13px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: REPORT_BORDER,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#94a3b8",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: REPORT_VIOLET,
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "13px",
      paddingTop: "8px",
      paddingBottom: "8px",
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
    },
    "& .MuiIconButton-root": {
      width: 28,
      height: 28,
      padding: 0,
      color: "#64748b",
      borderRadius: "6px",
      "&:hover": {
        backgroundColor: REPORT_VIOLET_LIGHT,
        color: REPORT_VIOLET,
      },
      "&.Mui-focusVisible": {
        backgroundColor: REPORT_VIOLET_LIGHT,
        color: REPORT_VIOLET,
      },
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  };

  const datePickerSlotProps = {
    textField: {
      size: "small",
      sx: datePickerSx,
    },
    openPickerButton: {
      sx: {
        width: 28,
        height: 28,
        padding: 0,
        borderRadius: "6px",
        color: "#64748b",
        "&:hover": {
          backgroundColor: REPORT_VIOLET_LIGHT,
          color: REPORT_VIOLET,
        },
        "&.Mui-focusVisible": {
          backgroundColor: REPORT_VIOLET_LIGHT,
          color: REPORT_VIOLET,
        },
      },
    },
    openPickerIcon: {
      sx: { fontSize: 20 },
    },
    popper: {
      sx: datePickerPopperSx,
    },
  };

  const handleCompanyChange = useCallback((event) => {
    const value = event.target.value;
    if (Array.isArray(value) && value.includes("__all__")) {
      setSelectedCompanyIds([]);
      setSelectedStoreIds([]);
      return;
    }
    const next = Array.isArray(value) ? value : [];
    setSelectedCompanyIds(next);
    setSelectedStoreIds((current) => {
      if (next.length === 0) return current;
      const allowedCompanyIds = new Set(next.map(String));
      const allowedStoreIds = new Set(
        (storeNames || [])
          .filter((store) => allowedCompanyIds.has(String(store.company)))
          .map((store) => String(store.id))
      );
      return current.filter((id) => allowedStoreIds.has(String(id)));
    });
  }, [storeNames]);

  const handleStoreChange = useCallback((event) => {
    const value = event.target.value;
    if (Array.isArray(value) && value.includes("__all__")) {
      setSelectedStoreIds([]);
      return;
    }
    setSelectedStoreIds(Array.isArray(value) ? value : []);
  }, []);

  const handleSourceChange = useCallback((event) => {
    const value = event.target.value;
    if (Array.isArray(value) && value.includes("__all__")) {
      if (selectedSources.length === SOURCE_OPTIONS.length) {
        setSelectedSources([]);
      } else {
        setSelectedSources(SOURCE_OPTIONS.map((source) => source.value));
      }
      return;
    }
    setSelectedSources(Array.isArray(value) ? value : []);
  }, [selectedSources.length]);

  const runReport = useCallback(async () => {
    if (endDate.isBefore(startDate, "day")) {
      setError("End date must be on or after start date.");
      return;
    }
    setIsLoading(true);
    setError(null);
    setHasRun(true);
    try {
      const res = await postRightworkAPI(API_get_assistant_question_report, {
        company_ids: selectedCompanyIds,
        store_ids: selectedStoreIds,
        sources: selectedSources.length ? selectedSources : [],
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      });
      setPayload(res?.data?.redux?.payload || null);
    } catch (e) {
      setError(
        e?.response?.data?.system?.message ||
          "Couldn't run the assistant questions report."
      );
      setPayload(null);
    } finally {
      setIsLoading(false);
    }
  }, [endDate, startDate, selectedCompanyIds, selectedStoreIds, selectedSources]);

  const filters = (
    <div className="flex flex-wrap items-end gap-4">
      <div className="w-[286px]">
        <div className="text-xs text-slate-500 mb-1">Start date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={startDate}
            onChange={(v) => v && setStartDate(v.startOf("day"))}
            slotProps={datePickerSlotProps}
          />
        </LocalizationProvider>
      </div>
      <div className="w-[286px]">
        <div className="text-xs text-slate-500 mb-1">End date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={endDate}
            onChange={(v) => v && setEndDate(v.startOf("day"))}
            slotProps={datePickerSlotProps}
          />
        </LocalizationProvider>
      </div>
      <div className="min-w-[190px]">
        <div className="text-xs text-slate-500 mb-1">Company</div>
        <Select
          multiple
          value={selectedCompanyIds}
          onChange={handleCompanyChange}
          input={<OutlinedInput size="small" sx={selectInputSx} />}
          renderValue={(selected) => renderMultiValue(selected, companyOptions, "companies", "company")}
          size="small"
          sx={selectSx}
          MenuProps={menuProps}
        >
          <MenuItem value="__all__" dense>
            <Checkbox checked={selectedCompanyIds.length === 0} sx={checkboxSx} />
            <ListItemText primary="All companies" />
          </MenuItem>
          {companyOptions.map((company) => (
            <MenuItem key={company.id} value={company.id} dense>
              <Checkbox checked={selectedCompanyIds.includes(company.id)} sx={checkboxSx} />
              <ListItemText primary={company.name} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="min-w-[190px]">
        <div className="text-xs text-slate-500 mb-1">Store</div>
        <Select
          multiple
          value={selectedStoreIds}
          onChange={handleStoreChange}
          input={<OutlinedInput size="small" sx={selectInputSx} />}
          renderValue={(selected) => renderMultiValue(selected, storeOptions, "stores", "store")}
          size="small"
          sx={selectSx}
          MenuProps={menuProps}
        >
          <MenuItem value="__all__" dense>
            <Checkbox checked={selectedStoreIds.length === 0} sx={checkboxSx} />
            <ListItemText primary="All stores" />
          </MenuItem>
          {storeOptions.map((store) => (
            <MenuItem key={store.id} value={store.id} dense>
              <Checkbox checked={selectedStoreIds.includes(store.id)} sx={checkboxSx} />
              <ListItemText primary={store.name} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="min-w-[190px]">
        <div className="text-xs text-slate-500 mb-1">Assistant</div>
        <Select
          multiple
          value={selectedSources}
          onChange={handleSourceChange}
          input={<OutlinedInput size="small" sx={selectInputSx} />}
          renderValue={(selected) => {
            if (!selected.length || selected.length === SOURCE_OPTIONS.length) return "All assistants";
            if (selected.length === 1) {
              return SOURCE_OPTIONS.find((source) => source.value === selected[0])?.label || "1 assistant";
            }
            return `${selected.length} assistants`;
          }}
          size="small"
          sx={selectSx}
          MenuProps={menuProps}
        >
          <MenuItem value="__all__" dense>
            <Checkbox
              checked={selectedSources.length === SOURCE_OPTIONS.length}
              indeterminate={selectedSources.length > 0 && selectedSources.length < SOURCE_OPTIONS.length}
              sx={checkboxSx}
            />
            <ListItemText primary="All assistants" />
          </MenuItem>
          {SOURCE_OPTIONS.map((source) => (
            <MenuItem key={source.value} value={source.value} dense>
              <Checkbox checked={selectedSources.includes(source.value)} sx={checkboxSx} />
              <ListItemText primary={source.label} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <Button
        variant="contained"
        size="small"
        startIcon={<PlayArrowIcon />}
        onClick={runReport}
        disabled={isLoading}
        sx={{
          height: 36,
          backgroundColor: REPORT_VIOLET,
          textTransform: "none",
          "&:hover": { backgroundColor: REPORT_VIOLET_DARK },
        }}
      >
        Run
      </Button>
    </div>
  );

  const summary = payload?.summary || null;
  const aiSummary = payload?.ai_summary || null;
  const interactions = payload?.interactions || [];

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={runReport}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : !hasRun ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          Run the report to load assistant questions for the selected filters.
        </div>
      ) : interactions.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No assistant questions match these filters.
        </div>
      ) : (
        <>
          {summary && (
            <div className="grid grid-cols-6 xl:grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-3 mb-4">
              <SummaryCard
                label="Questions"
                value={summary.total_questions}
                detail={
                  summary.test_question_count != null
                    ? `${summary.non_test_question_count || 0} non-Test · ${summary.test_question_count || 0} Test${summary.rows_truncated ? " · first 500 rows" : ""}`
                    : summary.rows_truncated
                      ? "Showing first 500 rows"
                      : null
                }
              />
              <SummaryCard label="Answered" value={formatPercent(summary.answered_rate)} detail={`${summary.answered_count || 0} answered`} />
              <SummaryCard
                label="Explicit gaps"
                value={formatPercent(summary.explicit_gap_rate ?? summary.gap_rate)}
                detail={`${summary.explicit_gap_count ?? ((summary.no_answer_count || 0) + (summary.error_count || 0) + (summary.unknown_count || 0))} help/content/capability gaps${
                  summary.help_miss_count || summary.kb_zero_result_count
                    ? ` · ${summary.help_miss_count || 0} help topic misses · ${summary.kb_zero_result_count || 0} doc searches with no results`
                    : ""
                }`}
              />
              <SummaryCard label="No answer / error" value={`${summary.no_answer_count || 0} / ${summary.error_count || 0}`} detail="Classified from answer text" />
              <SummaryCard label="Actions" value={`${summary.action_accepted_count || 0} / ${summary.action_canceled_count || 0}`} detail={`${summary.action_proposal_count || 0} proposed · ${summary.action_pending_count || 0} pending`} />
              <SummaryCard label="Feedback" value={`${summary.thumbs_up_count || 0} / ${summary.thumbs_down_count || 0}`} detail={`${summary.support_follow_up_review_count || 0} follow-up reviews`} />
            </div>
          )}

          {aiSummary && (
            <div className="mb-5 border border-slate-200 rounded-lg bg-white">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                <AutoAwesomeIcon sx={{ fontSize: 18, color: "#6d28d9" }} />
                <div>
                  <div className="text-sm font-semibold text-slate-900">AI summary</div>
                  <div className="text-xs text-slate-500">
                    {aiSummary.status === "complete"
                      ? `${aiSummary.sampled_question_count || 0} questions sampled`
                      : aiSummary.status}
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Summary</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{aiSummary.executive_summary}</p>
                </div>
                {aiSummary.status === "failed" && Array.isArray(aiSummary.caveats) && aiSummary.caveats.length > 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Failure detail: {truncate(aiSummary.caveats[0], 260)}
                  </div>
                )}
                {aiSummary.answer_quality && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Answer quality</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{aiSummary.answer_quality}</p>
                  </div>
                )}
                {Array.isArray(aiSummary.gap_themes) && aiSummary.gap_themes.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Gap themes</div>
                    <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                      {aiSummary.gap_themes.map((theme, index) => (
                        <div key={`${theme.theme || "theme"}-${index}`} className="border border-slate-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-slate-900">{theme.theme || "Theme"}</div>
                          <div className="text-xs text-slate-500 mt-1">{theme.question_count || 0} question{theme.question_count === 1 ? "" : "s"}</div>
                          <p className="text-xs text-slate-700 mt-2 leading-relaxed">{theme.evidence}</p>
                          <p className="text-xs text-slate-600 mt-2 leading-relaxed">{theme.recommended_action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {((summary.top_help_miss_queries || []).length > 0 ||
            (summary.top_kb_zero_result_queries || []).length > 0) && (
            <div className="mb-5 grid grid-cols-2 lg:grid-cols-1 gap-4">
              {(summary.top_help_miss_queries || []).length > 0 && (
                <div className="border border-slate-200 rounded-lg bg-white">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <div className="text-sm font-semibold text-slate-900">Help topic misses</div>
                    <div className="text-xs text-slate-500">Questions with no matching help topic. Candidates for new registry topics.</div>
                  </div>
                  <div className="p-3 space-y-1 max-h-64 overflow-y-auto">
                    {summary.top_help_miss_queries.map(([query, count], index) => (
                      <div key={`help-miss-${index}`} className="flex items-start justify-between gap-2 text-xs">
                        <span className="text-slate-700">{query}</span>
                        <span className="text-slate-400 shrink-0">×{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(summary.top_kb_zero_result_queries || []).length > 0 && (
                <div className="border border-slate-200 rounded-lg bg-white">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <div className="text-sm font-semibold text-slate-900">Document searches with no results</div>
                    <div className="text-xs text-slate-500">Content the company's uploaded documents could not answer. Candidates for missing SOP uploads.</div>
                  </div>
                  <div className="p-3 space-y-1 max-h-64 overflow-y-auto">
                    {summary.top_kb_zero_result_queries.map(([query, count], index) => (
                      <div key={`kb-miss-${index}`} className="flex items-start justify-between gap-2 text-xs">
                        <span className="text-slate-700">{query}</span>
                        <span className="text-slate-400 shrink-0">×{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-slate-700">
                  <th className="text-left font-semibold px-3 py-2 w-[145px]">Asked</th>
                  <th className="text-left font-semibold px-3 py-2 w-[125px]">Assistant</th>
                  <th className="text-left font-semibold px-3 py-2 w-[170px]">Location</th>
                  <th className="text-left font-semibold px-3 py-2 w-[130px]">Employee</th>
                  <th className="text-left font-semibold px-3 py-2 w-[105px]">Status</th>
                  <th className="text-left font-semibold px-3 py-2 w-[125px]">Action</th>
                  <th className="text-left font-semibold px-3 py-2 w-[105px]">Feedback</th>
                  <th className="text-left font-semibold px-3 py-2 min-w-[240px]">Question</th>
                  <th className="text-left font-semibold px-3 py-2 min-w-[280px]">Answer</th>
                  <th className="text-left font-semibold px-3 py-2 w-[80px]">Sources</th>
                  <th className="text-left font-semibold px-3 py-2 w-[120px]">Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {interactions.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 text-slate-600">{formatDateTime(row.created_at)}</td>
                    <td className="px-3 py-2 text-slate-700">{row.source_label || row.source}</td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="font-medium text-slate-800">{row.store_name || "Company-level"}</div>
                      <div className="text-slate-500">{row.company_name || "-"}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{row.user_name || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded border ${statusClass(row.answer_status)}`}>
                        {row.answer_status_label || row.answer_status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <ActionOutcomeCell row={row} />
                    </td>
                    <td className="px-3 py-2 text-slate-700">{row.feedback_rating_label || "-"}</td>
                    <td className="px-3 py-2 text-slate-800">
                      <FullTextCell value={row.question_text} max={260} />
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <FullTextCell value={row.answer_text} max={320} />
                    </td>
                    <td className="px-3 py-2 text-slate-500">{row.source_count || 0}</td>
                    <td className="px-3 py-2 text-slate-700">{row.support_follow_up_status_label || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PrebuiltReportShell>
  );
}
